import React, { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import { PlusCircleIcon } from "@heroicons/react/24/outline"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { GetParam, UpdateParam, CreateParam } from "aldoo-ra/Admin/ParamIO/api"
import Value from "aldoo-ra/Value"
import EnvironmentCard from "./EnvironmentCard.jsx"
import EnvironmentDialog from "./EnvironmentDialog.jsx"

const PARAM_KEY = "SITE_ENVIRONMENTS"
const POLL_INTERVAL = 2000

async function pingEnvironment(env) {
  const base = env.api.replace(/\/$/, "")
  const status = { online: false, auth: false }

  try {
    const onlineRes = await fetch(`${base}/v1/health`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ online: {} }),
      signal: AbortSignal.timeout(4000),
    })
    status.online = onlineRes.ok
  } catch {
    return status
  }

  try {
    const authRes = await fetch(`${base}/v1/health`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "aldoo-api-auth-key": env.api_key,
      },
      body: JSON.stringify({ environment: {} }),
      signal: AbortSignal.timeout(4000),
    })
    const data = await authRes.json()
    status.auth = authRes.ok && data?.result === true
  } catch {
    // online but auth failed — keep status.auth = false
  }

  return status
}

export default function EnvironmentsOverview() {
  const [userProfile] = Value("user-profile")
  const [environments, setEnvironments] = useState([])
  const [loading, setLoading] = useState(true)
  const [showDialog, setShowDialog] = useState(false)
  const [editingEnvironment, setEditingEnvironment] = useState(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [searchQuery, setSearchQuery] = useState("")
  const [envStatus, setEnvStatus] = useState({})

  // Load environments from ParamIO
  useEffect(() => {
    loadEnvironments()
  }, [refreshKey])

  // Poll all environments every 2 seconds
  useEffect(() => {
    if (environments.length === 0) return

    const poll = async () => {
      const results = await Promise.all(
        environments.map(async (env) => {
          const status = await pingEnvironment(env)
          return [env.id, status]
        })
      )
      setEnvStatus(Object.fromEntries(results))
    }

    poll()
    const timer = setInterval(poll, POLL_INTERVAL)
    return () => clearInterval(timer)
  }, [environments])

  const loadEnvironments = async () => {
    setLoading(true)
    try {
      console.log("[Environments] Loading from ParamIO with key:", PARAM_KEY)
      const response = await GetParam(PARAM_KEY)
      console.log("[Environments] GetParam response:", response)

      // GetParam returns the value directly on response.value, not response.data.value
      if (response?.value) {
        const envs = Array.isArray(response.value) ? response.value : []
        console.log("[Environments] Setting environments:", envs)
        setEnvironments(envs)
      } else {
        // Initialize with empty array if not found
        console.log("[Environments] No data found, initializing empty array")
        setEnvironments([])
      }
    } catch (error) {
      console.error("[Environments] Error loading environments:", error)
      showNotification({
        message: "Failed to load environments",
        className: "bg-admin_error text-white",
      })
      setEnvironments([])
    } finally {
      setLoading(false)
    }
  }

  const saveEnvironments = async (newEnvironments) => {
    try {
      console.log("[Environments] Saving environments:", newEnvironments)
      const author = userProfile?.email
        ? `${userProfile.name || userProfile.email.split("@")[0]} ${userProfile.email}`
        : "admin"

      // Check if parameter exists
      const existingParam = await GetParam(PARAM_KEY)
      console.log("[Environments] Existing param:", existingParam)

      if (existingParam?._id) {
        // Update existing (GetParam returns the param directly with _id)
        console.log("[Environments] Updating existing param")
        const updateResult = await UpdateParam({
          address: PARAM_KEY,
          value: newEnvironments,
          author,
        })
        console.log("[Environments] Update result:", updateResult)
      } else {
        // Create new
        console.log("[Environments] Creating new param")
        const createResult = await CreateParam({
          address: PARAM_KEY,
          value: newEnvironments,
          description: "Site deployment environments configuration",
          author,
        })
        console.log("[Environments] Create result:", createResult)
      }

      showNotification({
        message: "Environments saved successfully",
        className: "bg-admin_success text-white",
      })
      console.log("[Environments] Incrementing refresh key")
      setRefreshKey((prev) => prev + 1)
      return true
    } catch (error) {
      console.error("[Environments] Error saving environments:", error)
      showNotification({
        message: "Failed to save environments",
        className: "bg-admin_error text-white",
      })
      return false
    }
  }

  const handleAdd = () => {
    setEditingEnvironment(null)
    setShowDialog(true)
  }

  const handleEdit = (environment) => {
    setEditingEnvironment(environment)
    setShowDialog(true)
  }

  const handleDelete = async (environment) => {
    const confirmed = await showPromptDialog({
      title: "Delete Environment",
      message: `Are you sure you want to delete "${environment.name}"?`,
      yesLabel: "Delete",
      noLabel: "Cancel",
    })

    if (confirmed) {
      const updatedEnvironments = environments.filter((env) => env.id !== environment.id)
      await saveEnvironments(updatedEnvironments)
    }
  }

  const handleSave = async (environmentData) => {
    let updatedEnvironments

    if (editingEnvironment) {
      // Update existing
      updatedEnvironments = environments.map((env) =>
        env.id === editingEnvironment.id ? environmentData : env
      )
    } else {
      // Add new
      updatedEnvironments = [...environments, environmentData]
    }

    const success = await saveEnvironments(updatedEnvironments)
    if (success) {
      setShowDialog(false)
      setEditingEnvironment(null)
    }
  }

  return (
    <div className="h-full flex flex-col bg-admin_canvas">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-admin_bordered bg-admin_paper">
        <div>
          <Typography 
            component="h1"
            variant="admin_h3"
          >
            Site Environments
          </Typography>

          <Typography variant="admin_txt1_dim">
            Manage environments where this site is installed
          </Typography>
        </div>

        <div className="flex items-center gap-4">
          <Input
            variant="admin"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery("")}
            placeholder="Search environments..."
            showClearButton={true}
            className="w-64"
          />

          <Button 
            variant="admin_primary" 
            onClick={handleAdd}
          >
            <PlusCircleIcon className="w-5 h-5 mr-1" />
            Add Environment
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="text-admin_text_secondary">Loading...</div>
        ) : environments.filter(
            (env) =>
              !searchQuery ||
              env.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
              env.api?.toLowerCase().includes(searchQuery.toLowerCase()) ||
              env.id?.toLowerCase().includes(searchQuery.toLowerCase())
          ).length === 0 ? (
          <div className="text-admin_text_secondary text-center py-8">
            {searchQuery ? "No environments match your search" : "No environments configured"}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {environments
              .filter(
                (env) =>
                  !searchQuery ||
                  env.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  env.api?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  env.id?.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .map((environment) => (
                <EnvironmentCard
                  key={environment.id}
                  environment={environment}
                  status={envStatus[environment.id] ?? null}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
          </div>
        )}
      </div>

      {showDialog && (
        <EnvironmentDialog
          environment={editingEnvironment}
          existingEnvironments={environments}
          onSave={handleSave}
          onDelete={async (env) => {
            const updatedEnvironments = environments.filter((e) => e.id !== env.id)
            const success = await saveEnvironments(updatedEnvironments)
            if (success) {
              setShowDialog(false)
              setEditingEnvironment(null)
            }
          }}
          onClose={() => {
            setShowDialog(false)
            setEditingEnvironment(null)
          }}
        />
      )}
    </div>
  )
}
